import { inject, Injectable } from '@angular/core';
import { Meta, Title } from '@angular/platform-browser';
import { RouterEvent } from '@angular/router';
import { commonRoutes } from 'constants/web-app-constants';

type MetaTag = Record<string, string>;

@Injectable({
  providedIn: 'root',
})
export class MetaDataService {
  readonly #meta = inject(Meta);
  readonly #title = inject(Title);

  readonly #DEFAULT_ONBOARDING_TITLE = 'Depot eröffnen';
  readonly #TITLE_TAGS: MetaTag = {
    '/kunden-werben-kunden': 'Kunden werben Kunden',
    '/': 'Login',
    '/uebersicht': 'Übersicht',
    '/uebersicht/multi-order-strategien': 'Multi-Order-Strategien',
    '/meindepot/depotuebersicht': 'Depotübersicht',
    '/meindepot/kaufenverkaufen': 'Order',
    '/meindepot/kaufenverkaufen/multiorder': 'Multi-Order',
    '/watchlist': 'Watchlist',
    '/meindepot/kundenwerbenkunden': 'Empfehlen',
    '/posteingang': 'Posteingang',
    '/posteingang/tickets': 'Ticketsystem',
    '/verwaltung': 'Verwaltung',
    '/verwaltung/kryptohandelfreischaltung': 'Wallet eröffnen',
    '/verwaltung/kryptohandelfreischaltung/adressebestaetigen': 'Wallet eröffnen',
    '/verwaltung/kryptohandelfreischaltung/vertragabschliessen': 'Wallet eröffnen',
    '/meindepot/konto': 'Konto',
    '/handelbare-produkte': 'Handelbare Produkte',
    '/meindepot/einzahlen': 'Einzahlen',
    '/sparplaene': 'Sparpläne',
    '/sparplaene/sparplan-einrichten': 'Sparplan einrichten',
    '/sparplaene/sparplan-editieren': 'Sparplan editieren',
    '/sparplaene/sparplan-pruefen': 'Sparplan prüfen',
    '/krypto': 'Krypto',
    '/zustimmung': 'Zustimmung',
    '/depotwechsel-starten': 'Depotübertrag',
    '/hebelprodukte': 'Derivate finden',
    '/produkt': 'Wertpapier',
    '/bezugsrecht': 'Bezugsrecht',
    '/teilrecht': 'Teilrecht',
    '/guided-tour': 'Durchstarten',
    '/statistik': 'Meine Statistik (BETA)',
    '/depotkredit': 'Depotkredit',
    '/meine-orders': 'Meine Orders',
    '/trading': 'Trading',
    '/uebersicht/altersvorsorgedepot': 'Altersvorsorgedepot',
    '/etfs': 'ETF Überblick',
    '/aktien': 'Aktien Überblick',

    // Onboarding routes
    [commonRoutes.prelogin.onboarding.signUp]: 'Registrierung',
    [commonRoutes.prelogin.onboarding.enterPass]: 'Registrierung',
    [commonRoutes.prelogin.onboarding.emailConfirmation]: 'E-Mail bestätigen',
    [commonRoutes.prelogin.onboarding.loginAfterEmailConfirmation]: 'Einloggen',
    [commonRoutes.postlogin.onboarding.mobileNumber]: this.#DEFAULT_ONBOARDING_TITLE,
    [commonRoutes.postlogin.onboarding.person]: this.#DEFAULT_ONBOARDING_TITLE,
    [commonRoutes.postlogin.onboarding.residence]: this.#DEFAULT_ONBOARDING_TITLE,
    [commonRoutes.postlogin.onboarding.nationality]: this.#DEFAULT_ONBOARDING_TITLE,
    [commonRoutes.postlogin.onboarding.bankAccount]: this.#DEFAULT_ONBOARDING_TITLE,
    [commonRoutes.postlogin.onboarding.initialDeposit]: this.#DEFAULT_ONBOARDING_TITLE,
    [commonRoutes.postlogin.onboarding.provision]: this.#DEFAULT_ONBOARDING_TITLE,
    [commonRoutes.postlogin.onboarding.ident]: this.#DEFAULT_ONBOARDING_TITLE,
    [commonRoutes.postlogin.onboarding.inProgress]: this.#DEFAULT_ONBOARDING_TITLE,

    [commonRoutes.postlogin.onboarding.savingsPlan.favoriteSuggestions]: 'Sparplan anlegen',
    [commonRoutes.postlogin.onboarding.savingsPlan.top3Issuers]: 'Top 3 Anbieter',
    [commonRoutes.postlogin.onboarding.savingsPlan.alternative]: 'Beliebteste Sparpläne',
    [commonRoutes.postlogin.onboarding.savingsPlan.create]: 'Betrag eingeben',
    [commonRoutes.postlogin.onboarding.savingsPlan.confirm]: 'Sparplan prüfen',
    [commonRoutes.postlogin.onboarding.savingsPlan.success]: 'Sparplan angelegt',
    '/depot-eroeffnen/preview/sparplaene': 'Meine Sparpläne',

    [commonRoutes.postlogin.onboarding.investmentStrategy.choice]: 'Anlagestrategie',
    [commonRoutes.postlogin.onboarding.investmentStrategy.success]: 'Anlagestrategie',

    [commonRoutes.postlogin.child_depot]: 'Kinderdepot',
    [commonRoutes.postlogin.child_onboarding.person]: 'Kind anlegen',
  };

  readonly #DESCRIPTION_TAGS: MetaTag = {
    '/kunden-werben-kunden':
      'Einfach personalisierten Link an potentielle Neukunden verschicken und bei Eröffnung und aktiver Nutzung attraktive Prämie sichern.',
    '/': 'Herzlich willkommen bei finanzen.net zero, dem Online Broker mit 0 € Gebühren. Logge Dich ein und trade ohne Ordergebühren.',
  };

  public setMetaTags = (routerEvent: RouterEvent): void => {
    let url = routerEvent.url;
    url = url.split('?')[0];
    url = url.split('#')[0];
    if (!this.#meta) {
      return;
    }

    this.#setTitleTag(url);
    this.#setDescriptionTag(url);
    this.#setSocialMediaTags();
  };

  public setTitle = (title: string): void => {
    title += ' | finanzen.net zero';
    this.#title.setTitle(title);
    this.#setTag(title, 'twitter:title');
    this.#setTag(title, 'og:title');
  };

  #setTag(content: string, name?: string): void {
    const shouldUpdateTag = (name.indexOf(':') < 0 && this.#meta.getTag(`name=${name}`) != null) || name.indexOf(':') > 0;

    if (shouldUpdateTag) {
      this.#meta.updateTag({ name, property: name, content });
    } else {
      this.#meta.addTag({ name, property: name, content });
    }
  }

  #setDescriptionTag(url: string): void {
    const description = this.#DESCRIPTION_TAGS[url] || this.#DESCRIPTION_TAGS['/'];
    this.#setTag(description, 'description');
    this.#setTag(description, 'twitter:description');
    this.#setTag(description, 'og:description');
  }

  #setTitleTag(url: string): void {
    let title = this.#TITLE_TAGS[url];
    if (!title) {
      url = url.substring(0, url.lastIndexOf('/'));
      title = this.#TITLE_TAGS[url] ? this.#TITLE_TAGS[url] : this.#TITLE_TAGS['/']; // fallback
    }
    this.setTitle(title);
  }

  #setSocialMediaTags(): void {
    const socialMediaImage = 'https://mein.finanzen-zero.net/assets/img/gb-finnet-zero-logo-nl.png';

    this.#setTag(socialMediaImage, 'og:image');
    this.#setTag(socialMediaImage, 'twitter:image');
    this.#setTag('summary', 'twitter:card');
    this.#setTag('website', 'og:type');
  }
}
